// frontend/src/components/sales/PreOrderFlowModal.tsx
import React from 'react';
import { X, Package, CreditCard } from 'lucide-react';

export type PreOrderFlow = 'layaway' | 'fullPayment' | null;

interface PreOrderFlowModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (flow: PreOrderFlow) => void;
    productName?: string;
}

export const PreOrderFlowModal: React.FC<PreOrderFlowModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    productName
}) => {
    const [selectedFlow, setSelectedFlow] = React.useState<PreOrderFlow>(null);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (selectedFlow) {
            onSelect(selectedFlow);
            setSelectedFlow(null); // Reset for next time
        }
    };

    const handleClose = () => {
        setSelectedFlow(null);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-yellow-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                            Producto Pre-order
                        </h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {productName && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                                <strong>{productName}</strong> requiere pedido (sin stock disponible)
                            </p>
                        </div>
                    )}

                    <p className="text-gray-700 mb-6">
                        ¿Cómo deseas proceder con este producto?
                    </p>

                    {/* Flow Options */}
                    <div className="space-y-3 mb-6">
                        {/* Layaway Option */}
                        <button
                            onClick={() => setSelectedFlow('layaway')}
                            className={`w-full p-4 rounded-lg border-2 transition-all text-left ${selectedFlow === 'layaway'
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300 bg-white'
                                }`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedFlow === 'layaway'
                                        ? 'border-purple-500 bg-purple-500'
                                        : 'border-gray-300'
                                    }`}>
                                    {selectedFlow === 'layaway' && (
                                        <div className="w-2 h-2 bg-white rounded-full" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">
                                        Apartar con anticipo
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        La cliente paga un anticipo mínimo del 50% y liquida el resto cuando llegue el producto.
                                    </p>
                                </div>
                            </div>
                        </button>

                        {/* Full Payment Option */}
                        <button
                            onClick={() => setSelectedFlow('fullPayment')}
                            className={`w-full p-4 rounded-lg border-2 transition-all text-left ${selectedFlow === 'fullPayment'
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-200 hover:border-green-300 bg-white'
                                }`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedFlow === 'fullPayment'
                                        ? 'border-green-500 bg-green-500'
                                        : 'border-gray-300'
                                    }`}>
                                    {selectedFlow === 'fullPayment' && (
                                        <div className="w-2 h-2 bg-white rounded-full" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                                        Pagar completo ahora
                                        <CreditCard className="w-4 h-4" />
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        La cliente paga el 100% del total desde el inicio. El producto se entrega cuando llegue.
                                    </p>
                                </div>
                            </div>
                        </button>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                        <p className="text-xs text-blue-800">
                            <strong>Nota:</strong> En ambos casos, la venta se registra como Pre-order y el producto se entregará cuando esté disponible.
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <button
                            onClick={handleClose}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={!selectedFlow}
                            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${selectedFlow
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            Continuar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
